/**
 * ExcelChartML authoring/editing reference for the editChart tool.
 *
 * This is the authoritative syntax guide for chartXml in v2.
 */
export const EXCEL_CHART_XML_REFERENCE = `
## ExcelChartML Reference (v2, authoritative)

Excel charts are edited as real XML (NOT JSON-in-CDATA).

## Quick rules (read first)

1. Always start edit loops with \`readExcel.readCharts\`.
2. Use exact, unique \`search\` strings (pipeline rejects 0 or >1 matches).
3. Keep edits local (one concern per replacement whenever possible).
4. Re-read after each edit and continue from latest \`chartXml\`.
5. \`chartXml\` is real XML only. Never embed JSON.
6. \`<fyi>\` is informational; do not target it in search/replace.
7. Do NOT wrap the full chart document in outer CDATA (\`<![CDATA[ ... ]]>\`). Backend/tool may auto-strip it as recovery, but treat that as a warning path.

## Root and top-level semantics

\`\`\`xml
<excelChart version="2.0"
            chartId="Revenue Chart"
            sheetName="Dashboard"
            chartType="columnStacked"
            dataMode="point-level">
  ...
</excelChart>
\`\`\`

- [EDIT] \`chartId\`, \`chartType\`
- [RO-ish] \`version\` should stay \`2.0\`
- [RO runtime] \`sheetName\` should match the target sheet
- \`dataMode\` is resolved by backend (\`point-level\` or \`summary-style\`)

## Complete CREATE payload (recommended template)

Use this as a baseline when \`action="create"\`.

\`\`\`xml
<excelChart version="2.0" chartId="Revenue vs Margin" sheetName="Dashboard" chartType="columnClustered" dataMode="point-level">
  <defs>
    <fill id="f_rev" color="#4472C4"/>
    <fill id="f_margin" color="#ED7D31"/>
    <outline id="o_none"/>
    <line id="ln_margin" color="#ED7D31" width="2"/>
    <line id="ln_trend" color="#7F7F7F" width="1"/>
    <marker id="mk_margin" style="circle" size="6">
      <fill color="#FFFFFF"/>
      <outline color="#ED7D31" width="1"/>
    </marker>
  </defs>

  <placement anchorCell="F2" widthPixels="720" heightPixels="420"/>

  <dataSource categoryRange="Dashboard!A2:A10">
    <series index="0" nameRange="Dashboard!B1" valuesRange="Dashboard!B2:B10"/>
    <series index="1" nameRange="Dashboard!C1" valuesRange="Dashboard!C2:C10"/>
  </dataSource>

  <title show="1">
    <text><![CDATA[Revenue vs Margin]]></text>
    <textFormat fontFamily="Calibri" fontSize="14" bold="1" color="#111111"/>
  </title>

  <legend show="1" position="bottom" overlay="0"/>

  <series index="0" name="Revenue" chartType="columnClustered" axisGroup="primary">
    <fill ref="f_rev"/>
    <outline ref="o_none"/>
    <dataLabels show="1" showValue="1" position="outsideEnd" formatCode="#,##0"/>
  </series>

  <series index="1" name="Margin %" chartType="line" axisGroup="secondary">
    <line ref="ln_margin"/>
    <marker ref="mk_margin"/>
    <dataLabels show="1" showValue="1" formatCode="0.0%"/>
    <trendlines>
      <trendline type="linear" displayEquation="0" displayRSquared="0">
        <line ref="ln_trend"/>
      </trendline>
    </trendlines>
    <errorBarsY type="fixedValue" direction="both" value="0.02" endStyle="cap"/>
  </series>

  <categoryAxis visible="1">
    <majorGridlines show="0"/>
    <tickLabels position="nextToAxis"/>
  </categoryAxis>

  <primaryValueAxis visible="1">
    <title show="1"><text><![CDATA[Revenue ($M)]]></text></title>
    <bounds minimum="0" autoMaximum="1"/>
    <majorGridlines show="0"/>
  </primaryValueAxis>

  <secondaryValueAxis visible="1">
    <title show="1"><text><![CDATA[Margin %]]></text></title>
    <bounds minimum="0" maximum="1" autoMinimum="0" autoMaximum="0"/>
    <majorGridlines show="0"/>
    <tickLabels formatCode="0%"/>
  </secondaryValueAxis>

  <chartArea>
    <fill color="#FFFFFF"/>
    <outline ref="o_none"/>
  </chartArea>
  <plotArea>
    <fill color="#FFFFFF"/>
    <outline ref="o_none"/>
  </plotArea>

  <barColumnOptions gapWidth="120" overlap="0"/>

  <fyi>
    dataMode resolved to point-level values because the chart range is compact.
  </fyi>
</excelChart>
\`\`\`

## Summary-style mode example

For large ranges, backend may return \`dataMode="summary-style"\`.

\`\`\`xml
<excelChart version="2.0" chartId="Large Revenue" sheetName="RawData" chartType="line" dataMode="summary-style">
  <dataSource categoryRange="RawData!A2:A2000">
    <series index="0" nameRange="RawData!B1" valuesRange="RawData!B2:B2000"/>
  </dataSource>
  <series index="0" name="Revenue" chartType="line" axisGroup="primary">
    <line color="#4472C4" width="2"/>
  </series>
  <fyi>
    dataMode resolved to summary-style values because the chart range is large.
  </fyi>
</excelChart>
\`\`\`

## Defs, refs, and marker nesting

Supported def kinds:
- \`<fill id="..."/>\`
- \`<outline id="..."/>\`
- \`<line id="..."/>\`
- \`<marker id="..."> ... </marker>\`

Marker defs may contain nested style children:

\`\`\`xml
<marker id="mk1" style="diamond" size="7">
  <fill color="#FFFFFF"/>
  <outline color="#4472C4" width="1.2"/>
</marker>
\`\`\`

Reference defs from style nodes:
- \`<fill ref="f1"/>\`
- \`<outline ref="o1"/>\`
- \`<line ref="ln1"/>\`
- \`<marker ref="mk1"/>\`

Important: editing a def affects all \`ref\` users.

## Editable sections and fields

### Placement
- [EDIT] \`anchorCell\`, \`widthPixels\`, \`heightPixels\`

\`\`\`xml
<placement anchorCell="F2" widthPixels="640" heightPixels="360"/>
\`\`\`

### Data source
- [EDIT] \`categoryRange\`
- [EDIT] \`series[index,nameRange,name,valuesRange]\`

\`\`\`xml
<dataSource categoryRange="Dashboard!A2:A10">
  <series index="0" nameRange="Dashboard!B1" valuesRange="Dashboard!B2:B10"/>
</dataSource>
\`\`\`

### Series
- [EDIT] root attrs: \`index\`, \`name\`, \`chartType\`, \`axisGroup\`, \`smooth\`, \`explosion\`
- [EDIT] style children: \`fill\`, \`outline\`, \`line\`, \`marker\`
- [EDIT] labels: \`dataLabels\`
- [EDIT] \`trendlines\`, \`errorBarsX\`, \`errorBarsY\`
- [EDIT] \`dataPoints\` point-level overrides

\`\`\`xml
<series index="0" name="Revenue" chartType="columnClustered" axisGroup="primary">
  <fill ref="f_rev"/>
  <outline ref="o_none"/>
  <dataLabels show="1" showValue="1" showCategoryName="0" showSeriesName="0" showPercentage="0" showLeaderLines="0" position="outsideEnd" formatCode="#,##0"/>
  <trendlines>
    <trendline type="linear" order="2" displayEquation="0" displayRSquared="1">
      <line color="#7F7F7F" width="1"/>
    </trendline>
  </trendlines>
  <errorBarsY type="fixedValue" direction="both" value="1000" endStyle="cap"/>
  <dataPoints>
    <point pointIndex="2">
      <fill color="#FFC000"/>
      <dataLabel show="1" showValue="1" formatCode="#,##0"/>
    </point>
  </dataPoints>
</series>
\`\`\`

### Axes
- [EDIT] axis attrs: \`visible\`, \`crosses\`, \`displayUnits\`, \`logBase\`, \`displayUnitsLabel\`, \`logarithmicScale\`, \`valuesInReverseOrder\`
- [EDIT] axis children: \`title\`, \`bounds\`, \`units\`, \`majorGridlines\`, \`minorGridlines\`, \`majorTickMark\`, \`minorTickMark\`, \`tickLabels\`, \`line\`, \`textFormat\`

\`\`\`xml
<primaryValueAxis visible="1" displayUnits="none">
  <title show="1"><text><![CDATA[Revenue]]></text></title>
  <bounds minimum="0" autoMaximum="1"/>
  <units major="5000" autoMinor="1"/>
  <majorGridlines show="0"/>
  <minorGridlines show="0"/>
  <majorTickMark v="outside"/>
  <minorTickMark v="none"/>
  <tickLabels formatCode="#,##0" position="nextToAxis" rotation="0">
    <textFormat fontFamily="Calibri" fontSize="10"/>
  </tickLabels>
  <line color="#A6A6A6" width="1"/>
</primaryValueAxis>
\`\`\`

### Area blocks
- [EDIT] \`chartArea\`, \`plotArea\` style nodes (\`fill\`, \`outline\`)

\`\`\`xml
<chartArea><fill color="#FFFFFF"/><outline ref="o_none"/></chartArea>
<plotArea><fill color="#FFFFFF"/><outline ref="o_none"/></plotArea>
\`\`\`

### Chart options
- [EDIT] \`barColumnOptions\`, \`pieOptions\`, \`lineOptions\`, \`waterfallOptions\`

\`\`\`xml
<barColumnOptions gapWidth="140" overlap="-20"/>
<pieOptions rotation="90" holeSize="55"/>
<lineOptions smoothLines="1"/>
<waterfallOptions showConnectorLines="1" increaseColor="#4472C4" decreaseColor="#ED7D31" totalColor="#70AD47"/>
\`\`\`

## Add/remove series workflow

When adding or removing a series, update both:
1. \`<dataSource>\` ranges
2. corresponding root-level \`<series index="...">\` block

If you add only one side, execution may be inconsistent.

## Search/replace patterns (safe examples)

### 1) Change shared fill color
\`\`\`json
{
  "search": "<fill id=\\"f_rev\\" color=\\"#4472C4\\"/>",
  "replace": "<fill id=\\"f_rev\\" color=\\"#1F4E79\\"/>"
}
\`\`\`

### 2) Move and resize
\`\`\`json
{
  "search": "<placement anchorCell=\\"F2\\" widthPixels=\\"720\\" heightPixels=\\"420\\"/>",
  "replace": "<placement anchorCell=\\"B18\\" widthPixels=\\"760\\" heightPixels=\\"430\\"/>"
}
\`\`\`

### 3) Root chart type change
\`\`\`json
{
  "search": "chartType=\\"columnClustered\\"",
  "replace": "chartType=\\"barClustered\\""
}
\`\`\`

### 4) Convert one series to secondary-axis line
\`\`\`json
{
  "search": "<series index=\\"1\\" name=\\"Margin %\\" chartType=\\"columnClustered\\" axisGroup=\\"primary\\">",
  "replace": "<series index=\\"1\\" name=\\"Margin %\\" chartType=\\"line\\" axisGroup=\\"secondary\\">"
}
\`\`\`

### 5) Toggle major gridlines off
\`\`\`json
{
  "search": "<majorGridlines show=\\"1\\"/>",
  "replace": "<majorGridlines show=\\"0\\"/>"
}
\`\`\`

### 6) Add legend overlay
\`\`\`json
{
  "search": "<legend show=\\"1\\" position=\\"bottom\\" overlay=\\"0\\"/>",
  "replace": "<legend show=\\"1\\" position=\\"bottom\\" overlay=\\"1\\"/>"
}
\`\`\`

### 7) Axis title text edit
\`\`\`json
{
  "search": "<text><![CDATA[Revenue ($M)]]></text>",
  "replace": "<text><![CDATA[Revenue (USD, $M)]]></text>"
}
\`\`\`

### 8) Data label format code update
\`\`\`json
{
  "search": "formatCode=\\"#,##0\\"",
  "replace": "formatCode=\\"$#,##0.0\\""
}
\`\`\`

### 9) Add trendline block to a series
\`\`\`json
{
  "search": "</series>",
  "replace": "<trendlines><trendline type=\\"linear\\" displayEquation=\\"0\\" displayRSquared=\\"0\\"><line color=\\"#7F7F7F\\" width=\\"1\\"/></trendline></trendlines></series>"
}
\`\`\`

### 10) Update marker def nesting
\`\`\`json
{
  "search": "<marker id=\\"mk_margin\\" style=\\"circle\\" size=\\"6\\"><fill color=\\"#FFFFFF\\"/><outline color=\\"#ED7D31\\" width=\\"1\\"/></marker>",
  "replace": "<marker id=\\"mk_margin\\" style=\\"diamond\\" size=\\"7\\"><fill color=\\"#FFFFFF\\"/><outline color=\\"#ED7D31\\" width=\\"1.2\\"/></marker>"
}
\`\`\`

## Common chartType names

\`columnClustered\`, \`columnStacked\`, \`columnStacked100\`, \`barClustered\`, \`barStacked\`, \`barStacked100\`,
\`line\`, \`lineMarkers\`, \`lineStacked\`, \`lineStacked100\`, \`area\`, \`areaStacked\`, \`areaStacked100\`,
\`pie\`, \`doughnut\`, \`scatter\`, \`scatterLines\`, \`scatterLinesNoMarkers\`, \`scatterSmooth\`,
\`scatterSmoothNoMarkers\`, \`bubble\`, \`radar\`, \`radarFilled\`, \`waterfall\`, \`funnel\`, \`treemap\`,
\`sunburst\`, \`histogram\`, \`boxWhisker\`.

## Workflow and validation rules

1. Read first with \`readExcel.readCharts\`.
2. Use \`changes\` for edit and include \`readId\` when available.
3. Keep searches exact and unambiguous.
4. Re-read after each write before next edit.
5. For create, send complete valid XML.

## Pitfalls and failure modes

- Do NOT use JSON in \`chartXml\`.
- Do NOT wrap the entire \`chartXml\` document in \`<![CDATA[...]]>\` (auto-unwrapped as recovery, but still considered malformed authoring).
- Do NOT rely on a standalone move action (use \`<placement>\`).
- Do NOT target \`<fyi>\` in search/replace (volatile advisory text).
- Do NOT use broad fragments that match multiple places.
- Def edits are global to all \`ref\` users.
- Office.js limitation: true waterfall \`IsTotal\` semantics are not directly writable; \`waterfallOptions.totalPoints\` may require COM fallback.
- Office.js limitation: error-bar magnitude fields (\`value\`, \`plusValues\`, \`minusValues\`) are not fully writable via chart APIs.
- Office.js limitation: fill transparency is not directly writable (use solid fill or clear/no-fill semantics).
- For create, include explicit white fills on \`chartArea\` and \`plotArea\` to avoid ambiguous no-fill rendering.
- If adding/removing series, update both \`<dataSource>\` and root-level \`<series>\`.
- Keep XML well-formed (quoted attrs, valid nesting, proper closing tags).
`;
